import { inject } from '@angular/core';
import { Routes, Router } from '@angular/router';
import { authGuard } from './core/guards/auth.guard';
import { anyRoleGuard, landingRedirectGuard, roleGuard } from './core/guards/role.guard';
import { UN_ECRAN_PAR_DOSSIER } from './core/routing/un-ecran-par-dossier.strategy';

/**
 * Rôles admis sur les écrans du plan de charges CAC. Le Secrétariat CAEEP les tient ; les trois autres
 * les consultent (permission serveur `cac:read`, portée ALL).
 */
/**
 * Rôles admis sur les écrans CAC / CAEEP.
 *
 * <p>`CONSEIL_CCE` en est RETIRÉ — arbitrage du CCE, 2026-08-08 : aucun lien entre le Conseil et la
 * commission. Il gardait l'accès aux 19 routes de l'espace CAEEP, dont le plan de charges et les
 * dossiers de nomination.
 *
 * <p>Cela ferme aussi l'écran par lequel le Conseil VALIDAIT les nominations —
 * {@code cac-nomination:valider}, qu'il était le seul rôle à porter. QUI valide est TRANCHÉ depuis
 * (arbitrage du 2026-08-08) : la proposition s'arrête en <b>séance de la CAEEP</b>, puis le Président
 * notifie l'avis à l'EEP, dont l'organe social nomme (circuit corrigé E1) — plus d'étape
 * `VALIDE_CONSEIL` dans l'automate. La permission est laissée en base — la retirer n'ajouterait rien
 * et rendrait le retour arrière plus lourd — mais elle n'a plus d'objet.
 */
const CAC_ROLES = anyRoleGuard(
  'SECRETARIAT_CAEEP',
  'PRESIDENT_CCE',
  'ADMIN_FONCTIONNEL',
);

/**
 * Routes de l'application.
 *
 * Toute route protégée par `authGuard` : un utilisateur non authentifié est redirigé vers la page
 * de login **Keycloak** (réalm `cce`, thème `cce`), où se déroulent le 2e facteur / E-Hawia / step-up.
 * Le retour de connexion est traité au démarrage (`checkAuth`, cf. app.config) avant le routage.
 *
 * Atterrissage par défaut : le Dashboard d'aiguillage de l'espace Contrôleur d'État.
 */
export const routes: Routes = [
  // Atterrissage PAR RÔLE : Président → /presidence, autres → /controleur (UrlTree du guard).
  { path: '', pathMatch: 'full', canActivate: [authGuard, landingRedirectGuard], children: [] },
  // « كل الإشعارات » : la page commune à tous les espaces derrière « عرض كل الإشعارات » (2026-09-16). Le
  // panneau ne montre que les plus récentes ; tout ce qui dépassait était inatteignable. Périmètre
  // garanti par le serveur (mon identité, mes rôles) : aucun rôle requis.
  {
    path: 'notifications',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/notifications/notifications-page').then((m) => m.NotificationsPage),
  },
  {
    path: 'controleur/dashboard',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/dashboard/controleur-dashboard').then(
        (m) => m.ControleurDashboardPage,
      ),
  },
  {
    path: 'controleur/profil',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/profil/profil').then((m) => m.ProfilPage),
  },
  {
    // CT-01 (CR 2026-07-24) : `الرزنامة` et `برنامج العمل الأسبوعي` ne font plus qu'UN écran. L'ancienne
    // adresse redirige vers l'écran fusionné (favoris et liens en circulation préservés).
    path: 'controleur/calendrier',
    pathMatch: 'full',
    redirectTo: 'controleur/programme-hebdo',
  },
  // ══ L'ACCUEIL DE L'A POSTERIORI — une entrée de menu, trois cartes (CCE, 2026-08-28) ══
  // La barre latérale portait un groupe de QUATRE entrées ; le CCE en veut UNE, qui ouvre une page
  // de cartes — la forme de l'espace de la خلية إدارية. Les écrans, eux, ne bougent pas : seul le
  // chemin pour y arriver change, et leurs routes restent atteignables directement.
  {
    path: 'controleur/posteriori',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/posteriori/posteriori-accueil').then((m) => m.PosterioriAccueilPage),
  },
  // Les DEUX rapports sur une page à eux : le CCE les a nommés séparément (D13), et ils ne
  // s'adressent pas aux mêmes destinataires — le préliminaire à la مؤسسة, le final au Président.
  {
    path: 'controleur/posteriori/rapports',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/posteriori/posteriori-rapports').then((m) => m.PosterioriRapportsPage),
  },
  // Espace B — Conduite a posteriori (« إنجاز المهمة ») : missions → séances → fiche de vérification.
  {
    path: 'controleur/missions',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/mission/mission-list').then((m) => m.MissionListPage),
  },
  // `المهمات المنجزة` — l'historique des missions achevées (CCE, 2026-08-27, lot G).
  //
  // ⚠️ SEGMENT FRÈRE, et non `controleur/missions/achevees`. Le routeur Angular résout dans l'ORDRE
  // DE DÉCLARATION : un sous-chemin déclaré après `controleur/missions/:missionId` serait capturé
  // par le paramètre, et l'écran de détail chargerait une mission d'identifiant « achevees ».
  // (Côté Spring c'est l'inverse — les chemins littéraux priment — d'où `/api/missions/achevees`.)
  {
    path: 'controleur/missions-achevees',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/mission/missions-achevees').then((m) => m.MissionsAcheveesPage),
  },
  {
    path: 'controleur/missions/:missionId',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/mission/mission-detail').then((m) => m.MissionDetailPage),
  },
  // Les observations ont leur propre écran : sept blocs par observation, et le CONTRADICTOIRE est
  // un moment distinct — on y revient des semaines après avoir rédigé le constat.
  {
    path: 'controleur/missions/:missionId/observations',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/mission/observations').then((m) => m.ObservationsPage),
  },
  // Les deux rapports partagent un écran : ce sont DEUX RENDUS du même corpus d'observations, pas
  // deux documents. Le préliminaire les envoie à l'EEP ; le final y ajoute ses réponses.
  {
    path: 'controleur/missions/:missionId/rapport',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/mission/rapport-mission').then((m) => m.RapportMissionPage),
  },
  {
    path: 'controleur/missions/:missionId/seances/:seanceId',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/mission/intervention-detail').then(
        (m) => m.InterventionDetailPage,
      ),
  },
  // Espace A — Contrôle permanent. CT-01/CT-02 : écran FUSIONNÉ `الرزنامة + برنامج العمل الأسبوعي`
  // (calendrier empilé au-dessus de la zone `البرمجة`). Le chemin historique est conservé pour ne
  // casser aucun lien de retour ni les sous-routes `/programme-hebdo/{nouveau,seances/:id}`.
  {
    path: 'controleur/programme-hebdo',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/permanent/plan-travail').then((m) => m.PlanTravailPage),
  },
  {
    // « إنجاز المهمة » (COPIL C7, essai A/B) : file des séances à conduire → deep-link phase Conduite.
    path: 'controleur/execution-mission',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/permanent/execution-mission').then((m) => m.ExecutionMissionPage),
  },
  {
    path: 'controleur/programme-hebdo/nouveau',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/permanent/insertion-evenement').then(
        (m) => m.InsertionEvenementPage,
      ),
  },
  {
    path: 'controleur/programme-hebdo/seances/:seanceId',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/permanent/permanent-seance-detail').then(
        (m) => m.PermanentSeanceDetailPage,
      ),
  },
  // Permanent — Rapport annuel du contrôleur (تقرير مراقب الدولة, Écran 7) : éditeur agrégé + signature.
  // Deep-link `…/rapport-annuel/:year/:id` (rechargeable au refresh) ; sans paramètre → liste.
  {
    path: 'controleur/rapport-annuel',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/rapport/rapport-annuel').then((m) => m.RapportAnnuelPage),
  },
  {
    path: 'controleur/rapport-annuel/:year/:id',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/rapport/rapport-annuel').then((m) => m.RapportAnnuelPage),
  },
  // Permanent — Rapport d'activité trimestriel du contrôleur (تقرير النشاط الثلاثي) : 3 volets agrégés.
  // Deep-link `…/rapport-trimestriel/:period/:id` (rechargeable au refresh) ; sans paramètre → liste.
  {
    path: 'controleur/rapport-trimestriel',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/rapport-trimestriel/rapport-trimestriel').then(
        (m) => m.RapportTrimestrielPage,
      ),
  },
  {
    path: 'controleur/rapport-trimestriel/:period/:id',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/rapport-trimestriel/rapport-trimestriel').then(
        (m) => m.RapportTrimestrielPage,
      ),
  },
  // Général — Fiche Entreprise / dossier permanent (« المؤسسات والمنشآت العمومية »).
  {
    path: 'controleur/entreprises',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/entreprise/entreprise-list').then((m) => m.EntrepriseListPage),
  },
  {
    path: 'controleur/entreprises/:id',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/entreprise/entreprise-fiche').then((m) => m.EntrepriseFichePage),
  },
  // Général — Bibliothèque documentaire (« الوثائق ») : recherche plein-texte + facettes (nature/labels/tags).
  // CT-08 (CR 2026-07-24) : plus d'entrée de menu (cf. main-layout.ts) ; route gardée accessible par URL —
  // seul point d'appel de GET /ged/search, à rebrancher ailleurs avant toute suppression définitive.
  {
    path: 'controleur/documents',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/ged/bibliotheque').then((m) => m.BibliothequePage),
  },
  // La bibliothèque n'est plus montée hors de l'espace Contrôleur : l'entrée Conseil (`conseil/documents`,
  // CONS-18) a été retirée le 2026-09-16, celle du Président (`presidence/documents`, « الوثائق ») le
  // 2026-10-04, toutes deux sur retour du CCE. Le Président garde « فضاء الوثائق والمعارف ».
  // Général — Espace documentaire partagé (فضاء الوثائق والمعارف) : arbre de catégories/sous-catégories,
  // recherche plein-texte, étagère de favoris, modération (Président) et administration de la taxonomie (Cellule).
  {
    path: 'controleur/espace-documentaire',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/controleur/documentary/documentary-space/documentary-space').then(
        (m) => m.DocumentarySpacePage,
      ),
  },
  {
    // Espace documentaire PARTAGÉ — alias Président (même composant) : garde le préfixe /presidence/*
    // pour que la sidebar contextuelle NE bascule PAS vers l'espace Contrôleur (régression corrigée).
    path: 'presidence/espace-documentaire',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/controleur/documentary/documentary-space/documentary-space').then(
        (m) => m.DocumentarySpacePage,
      ),
  },
  // Le MÊME espace documentaire dans les trois autres espaces — CAEEP, Cellule administrative,
  // Reporting (PO, 2026-09-07 : « comme dans l'espace contrôleur »). Un alias par préfixe, pour la
  // même raison que l'alias Président : la sidebar suit le préfixe, et un lien vers
  // /controleur/… aurait fait basculer l'agent d'espace en lisant un document. Lecture seule pour
  // ces rôles : le composant borne ses gestes par les rôles, le serveur par `documentary:*`.
  {
    path: 'caeep/espace-documentaire',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/controleur/documentary/documentary-space/documentary-space').then(
        (m) => m.DocumentarySpacePage,
      ),
  },
  {
    path: 'cellule/espace-documentaire',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/controleur/documentary/documentary-space/documentary-space').then(
        (m) => m.DocumentarySpacePage,
      ),
  },
  {
    path: 'reporting/espace-documentaire',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/controleur/documentary/documentary-space/documentary-space').then(
        (m) => m.DocumentarySpacePage,
      ),
  },
  // Général — Demandes d'appui (« طلبات المساندة ») : sollicitation d'avis (sans réponse claire en réglementation).
  // AD-05 (CR 2026-07-24) : compte rendu de formation, du contrôleur vers la cellule administrative.
  {
    path: 'controleur/demandes-appui',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/support/demandes-appui-list').then((m) => m.DemandesAppuiListPage),
  },
  {
    path: 'controleur/demandes-appui/:id',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/support/demande-appui-detail').then((m) => m.DemandeAppuiDetailPage),
  },
  // Général — Notes d'alerte (« مذكرة إعلام ») : signalement formel et confidentiel au Président.
  {
    path: 'controleur/notes-alerte',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/note-alerte/notes-alerte-list').then((m) => m.NotesAlerteListPage),
  },
  {
    path: 'controleur/notes-alerte/:id',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/note-alerte/note-alerte-detail').then((m) => m.NoteAlerteDetailPage),
  },
  // Les routes `controleur/recommandations` (§5.8) sont RETIRÉES — atelier du 2026-08-07. Aucune
  // redirection n'est laissée derrière : l'écran n'existe plus, et rediriger ferait croire à un
  // déplacement. Une URL en marque-page tombe sur le repli habituel.
  {
    path: 'demo',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/demo/checklist-demo').then((m) => m.ChecklistDemoPage),
  },
  // Pilote — provenance des valeurs auto-remplies (Partie II financière) : indicateur + popover + lien profond.
  {
    path: 'demo/provenance',
    canActivate: [authGuard],
    loadComponent: () =>
      import('./features/demo/provenance-demo').then((m) => m.ProvenanceDemoPage),
  },
  // Espace Président (رئاسة الهيئة) — suivi cross-contrôleur + décision sur les notes d'alerte.
  {
    path: 'presidence/dashboard',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/dashboard/presidence-dashboard').then(
        (m) => m.PresidenceDashboardPage,
      ),
  },
  {
    path: 'presidence/programme-hebdo',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/programme-hebdo/programme-hebdo').then(
        (m) => m.ProgrammeHebdoPage,
      ),
  },
  {
    // Détail d'une séance en CONSULTATION (PR-03, extension de D10) : le Président lit le même détail
    // que le contrôleur, sans pouvoir l'altérer. Route enfant du programme, dont elle est le drill-down.
    path: 'presidence/programme-hebdo/seances/:id',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/programme-hebdo/seance-lecture').then(
        (m) => m.PresidenceSeanceLecturePage,
      ),
  },
  // Rubrique « التواصل مع مراقبي الدولة » (2026-10-01) : les trois listes du Président (appui, notes, messagerie)
  // sont les ONGLETS d'un même hub. Chaque route garde son URL et monte le hub sur son onglet — notifications,
  // recherche globale et tableau de bord ouvrent le bon onglet sans rien changer. Les détails restent des pages.
  {
    path: 'presidence/notes-alerte',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    data: { onglet: 'notes' },
    loadComponent: () =>
      import('./features/presidence/echanges/presidence-echanges').then((m) => m.PresidenceEchangesPage),
  },
  {
    path: 'presidence/notes-alerte/:id',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/notes-alerte/note-alerte-review-detail').then(
        (m) => m.NoteAlerteReviewDetailPage,
      ),
  },
  // Validation du rapport annuel du contrôleur (REP-R04, validation unique du Président — D11 :
  // le Conseil sort du circuit de validation des rapports du contrôleur).
  {
    // Hub à onglets : rapports annuels (à valider) + rapports trimestriels (fusion des 2 anciennes entrées).
    path: 'presidence/rapports',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/rapports/presidence-rapports-hub').then(
        (m) => m.PresidenceRapportsHubPage,
      ),
  },
  {
    path: 'presidence/rapports/:id',
    data: { [UN_ECRAN_PAR_DOSSIER]: true },
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/rapports/presidence-rapport-review').then(
        (m) => m.PresidenceRapportReviewPage,
      ),
  },
  // Validation des rapports d'activité trimestriels des contrôleurs (تقرير النشاط الثلاثي) —
  // validation unique du Président (D11). Composant unique : inbox sans `:id`, revue avec `:id`.
  {
    path: 'presidence/rapports-trimestriels',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/rapports-trimestriels/presidence-rapports-trim').then(
        (m) => m.PresidenceRapportsTrimPage,
      ),
  },
  {
    path: 'presidence/rapports-trimestriels/:id',
    data: { [UN_ECRAN_PAR_DOSSIER]: true },
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/rapports-trimestriels/presidence-rapports-trim').then(
        (m) => m.PresidenceRapportsTrimPage,
      ),
  },
  // Cockpit annuel unifié : Plan de contrôle + Affectation par campagne (remplace les 2 listes).
  {
    path: 'presidence/programme',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/programme/programme-cockpit').then((m) => m.ProgrammeCockpitPage),
  },
  // Espace Président (رئاسة الهيئة) — INITIATION du plan d'affectation (ADR 0010 / PO-31) : le Président
  // ouvre le plan, affecte ligne par ligne, puis SOUMET au Conseil (dont la validation = publication).
  // Composants historiquement sous features/conseil/affectations (rôle inversé, fichiers non déplacés).
  {
    path: 'presidence/affectations',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/conseil/affectations/affectation-plans-list').then(
        (m) => m.AffectationPlansListPage,
      ),
  },
  {
    path: 'presidence/affectations/:id',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/conseil/affectations/affectation-builder').then((m) => m.AffectationBuilderPage),
  },
  {
    path: 'presidence/affectations/:id/lignes/:controlLineId/affecter',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/conseil/affectations/affecter-controleur-form').then(
        (m) => m.AffecterControleurFormPage,
      ),
  },
  {
    // Affectation d'un contrôleur متفرغ à une EEP du registre complet (préparation « toutes les EEP », Chantier 3).
    path: 'presidence/affectations/:id/eep/:enterpriseId/affecter',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/conseil/affectations/affecter-controleur-form').then(
        (m) => m.AffecterControleurFormPage,
      ),
  },
  {
    // Changement du contrôleur permanent en cours d'année (proposition du Président).
    path: 'presidence/affectations/:id/changer/:ligneId',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/conseil/affectations/affecter-controleur-form').then(
        (m) => m.AffecterControleurFormPage,
      ),
  },
  {
    // Ancienne page « المراقبون المتفرغون », fusionnée dans « قائمة مراقبي الدولة » (atelier CCE du 2026-10-01).
    // L'adresse reste servie : les notifications déjà émises (AffectationWorkflowService) y pointent.
    path: 'presidence/permanents',
    redirectTo: 'presidence/controleurs',
    pathMatch: 'full',
  },
  // ------------------------------------------------------------------------------------
  // Annuaire des contrôleurs (AD-02, CR 2026-07-24) — la fiche est commune ; la LISTE du Président est la
  // sienne depuis le 2026-10-02 (pilotage), la cellule et le reporting gardent `ControleursPage`. Le Conseil est écarté (décision du client du
  // 2026-08-06, en écart avec le CR qui visait trois espaces). L'habilitation qui compte est côté
  // serveur (`controller:read` en portée ALL, refus en 404) : la garde de rôle n'est là que pour
  // éviter d'afficher un écran vide à qui n'a rien à y voir.
  // ------------------------------------------------------------------------------------
  {
    // Le Président a SA liste : l'annuaire + le pilotage des affectations permanentes (atelier CCE du
    // 2026-10-01). La cellule et le reporting gardent la liste simple (`ControleursPage`).
    path: 'presidence/controleurs',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/controleurs/presidence-controleurs').then((m) => m.PresidenceControleursPage),
  },
  {
    path: 'presidence/controleurs/:id',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/annuaire/controleur-fiche').then((m) => m.ControleurFichePage),
  },
  // ===================================================================================
  // Espace Secrétariat CAEEP (كتابة لجنة التدقيق) — le CAC (مراقب الحسابات) est un professionnel
  // EXTERNE membre de l'OECT : cet espace n'a RIEN à voir avec l'affectation des contrôleurs d'État
  // (glossaire §8). La tenue du plan de charges revient au Secrétariat CAEEP (arbitrage Q5, atelier
  // interne) ; le Président, le Conseil et l'Admin fonctionnel y accèdent en LECTURE, parce qu'ils
  // portent `cac:read` côté serveur. D'où anyRoleGuard et non roleGuard : avec une garde mono-rôle,
  // ces trois rôles avaient l'API sans l'écran.
  // ===================================================================================
  // L'espace s'ouvre sur le جدول قيادة (décision Q11a du PO, 2026-09-29), plus sur le plan de charges.
  { path: 'caeep', pathMatch: 'full', redirectTo: 'caeep/pilotage' },
  {
    path: 'caeep/plan-charges',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-plan-charges').then((m) => m.CacPlanChargesPage),
  },
  // Pendant EXPERT du plan de charges (مخطط الأعباء, RG-401/RG-462) : le glossaire distingue les deux
  // vues, جدول التعهدات mesurant le cabinet et celle-ci l'expert. Lecture seule.
  {
    path: 'caeep/charge-experts',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-charge-experts').then((m) => m.CacChargeExpertsPage),
  },
  // Référentiel des experts-comptables (analyse du 2026-09-25) : la PERSONNE, son matricule et ses deux
  // écritures — le registre la nommait en latin, la structure permanente en arabe, rien ne les reliait.
  {
    path: 'caeep/experts',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () => import('./features/caeep/cac/cac-experts').then((m) => m.CacExpertsPage),
  },
  {
    path: 'caeep/experts/:id',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-expert-fiche').then((m) => m.CacExpertFichePage),
  },
  {
    path: 'caeep/cabinets/:id',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-cabinet-detail').then((m) => m.CacCabinetDetailPage),
  },
  // « الإسناد إلى جدول التعهدات » n'est plus un écran (retour CAEEP du 2026-09-22). L'agent lisait
  // « إسناد » (affecter) là où la page ne faisait que SIMULER une part par cabinet ; ses trois
  // services vivent ailleurs (projection d'éligibilité dans le dossier, mandat au référentiel par
  // مسك جدول التعهدات, ouverture du dossier par son formulaire). L'adresse mène désormais au geste
  // qu'il cherchait : les mandats en attente d'attribution du مخطط الأعباء — une redirection par
  // FONCTION, seule forme qui sache porter un paramètre de requête.
  {
    path: 'caeep/affectation',
    redirectTo: () =>
      inject(Router).createUrlTree(['/caeep/charge-experts'], { queryParams: { filtre: 'attente' } }),
  },
  {
    path: 'caeep/eep',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () => import('./features/caeep/cac/cac-eep').then((m) => m.CacEepPage),
  },
  // Vue prioritaire du tableau de bord CAEEP (demande CCE du 2026-07-30) : les mandats echus,
  // donc les EEP appelees a lancer une consultation. Voir docs/cac/dashboard-caeep.md.
  {
    path: 'caeep/mandats-echus',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-mandats-echus').then((m) => m.CacMandatsEchusPage),
  },
  // Vue n 3 du tableau de bord : capacite du vivier de cabinets, datee (RG-455) + alerte de
  // tension (RG-454). docs/cac/dashboard-caeep.md §5.
  {
    path: 'caeep/capacite-vivier',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-capacite-vivier').then((m) => m.CacCapaciteVivierPage),
  },
  // Configuration : bareme des honoraires (F26, arrete du 29/04/2022) et parametres par exercice
  // (F27, RG-415). Un parametre invisible se comporte comme la constante cachee qu'il remplace.
  // Pilotage : les 3 vues du tableau de bord qui n'ont pas d'écran dédié (F21b).
  {
    path: 'caeep/pilotage',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-pilotage').then((m) => m.CacPilotagePage),
  },
  // La fiche EEP en LECTURE depuis l'espace CAEEP (décision Q11c du PO, 2026-09-29) : identité, états
  // financiers et contrôleur d'État titulaire — l'écran borne ses onglets dans cet espace, le serveur
  // n'accorde que `enterprise:read`. Un alias par préfixe, comme l'espace documentaire : la barre
  // latérale suit le préfixe, et un lien vers /presidence/… ferait changer l'agent d'espace.
  {
    path: 'caeep/entreprises/:id',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/controleur/entreprise/entreprise-fiche').then((m) => m.EntrepriseFichePage),
  },
  {
    path: 'caeep/parametrage',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-parametrage').then((m) => m.CacParametragePage),
  },
  // Instruction d'un dossier : les cabinets projetes a la date de nomination (RG-421).
  // C'est l'ecran ou se lit le المتبقّي بعد الإسناد. Voir docs/cac/decision-rules.md §6.3.
  {
    path: 'caeep/eligibilite',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-eligibilite').then((m) => m.CacEligibilitePage),
  },
  // Instruction par lot : les nominations programmees a la meme date (RG-425) — le cumul par
  // cabinet, et la programmation de تاريخ التعيين (RG-424). docs/cac/decision-rules.md §6.3/§7.
  {
    path: 'caeep/instruction-lot',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-instruction-lot').then((m) => m.CacInstructionLotPage),
  },
  {
    path: 'caeep/anomalies',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-anomalies').then((m) => m.CacAnomaliesPage),
  },
  // Le circuit de nomination engage la CAEEP, le Conseil ET le Président : la même garde multi-rôles
  // que les autres écrans CAC convient, chaque autorité ne voyant que ses propres actions.
  //
  // UN COMPOSANT, TROIS ADRESSES. La barre latérale est contextuelle au PRÉFIXE D'URL : un membre du
  // Conseil qui ouvrirait `/caeep/nominations` verrait le menu du Secrétariat CAEEP à la place du sien.
  // Chaque autorité accède donc au même écran depuis son espace. Aucune duplication de composant —
  // seulement l'adresse change, et l'espace reste celui de l'utilisateur.
  {
    path: 'caeep/nominations',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-nominations').then((m) => m.CacNominationsPage),
  },
  // La PAGE d'un dossier (revue CAC du 2026-09-29, lot L4, maquette M1) : le même composant, sans la liste. Le lien
  // `?dossier=` reste valable — il y mène. Les écrans du dossier (/:id/depouillement…) sont déclarés après : une route
  // qui ne consomme pas toute l'URL ne s'applique pas, le routeur passe à la suivante.
  {
    path: 'caeep/nominations/:id',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-nominations').then((m) => m.CacNominationsPage),
  },
  // Depouillement d'une consultation : le PV recalcule depuis les offres persistees (RG-313/RG-322).
  {
    path: 'caeep/nominations/:id/depouillement',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-depouillement').then((m) => m.CacDepouillementPage),
  },
  // Fiche d'audit du dossier : les points du modele officiel, deux niveaux de saisie (RG-107) et
  // le verrou de completude (ADR 0008). docs/cac/verification-process.md.
  {
    path: 'caeep/nominations/:id/fiche-audit',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-fiche-audit').then((m) => m.CacFicheAuditPage),
  },
  // Fiche de designation بطاقة تعيين : les 9 rubriques du classeur CCE, calculees ou attestees.
  // docs/cac/documents.md §2.5.1.
  // Pieces du dossier (menu وثائق) : les scans, ranges comme au PV d'ouverture. Adosse la
  // verification de la بطاقة تدقيق a ses pieces.
  {
    path: 'caeep/nominations/:id/pieces',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-pieces').then((m) => m.CacPiecesPage),
  },
  {
    path: 'caeep/nominations/:id/fiche-designation',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-fiche-designation').then((m) => m.CacFicheDesignationPage),
  },
  // La route `conseil/nominations-cac` est RETIRÉE — arbitrage du CCE, 2026-08-08 : aucun écran de
  // nomination CAC dans l'espace Conseil. L'écran lui-même reste, monté sous `presidence` et `caeep` :
  // il n'a jamais été propre au Conseil, seulement monté sous trois chemins.
  {
    path: 'presidence/nominations-cac',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-nominations').then((m) => m.CacNominationsPage),
  },
  {
    path: 'presidence/nominations-cac/:id',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-nominations').then((m) => m.CacNominationsPage),
  },
  // Les écrans CAC ont d'abord été montés sous /presidence : on redirige pour ne pas casser les liens
  // déjà diffusés (documents d'atelier, guide de lecture).
  { path: 'presidence/cac', pathMatch: 'full', redirectTo: 'caeep/plan-charges' },
  { path: 'presidence/cac/plan-charges', redirectTo: 'caeep/plan-charges' },
  { path: 'presidence/cac/cabinets/:id', redirectTo: 'caeep/cabinets/:id' },
  { path: 'presidence/cac/eep', redirectTo: 'caeep/eep' },
  { path: 'presidence/cac/anomalies', redirectTo: 'caeep/anomalies' },
  // Entrée unique du plan de contrôle = la page « برنامج الرقابة السنوي » (une année à la fois).
  { path: 'presidence/plans', pathMatch: 'full', redirectTo: 'presidence/programme' },
  {
    // L'ancienne route du composer ouvre la page du programme sur l'année de CE programme (maquette du
    // 2026-09-30 : le tableau est l'écran) — les liens existants (sélecteur de contrôleur…) y retombent.
    path: 'presidence/plans/:id/composer',
    data: { [UN_ECRAN_PAR_DOSSIER]: true },
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/programme/programme-cockpit').then((m) => m.ProgrammeCockpitPage),
  },
  {
    // Workflow fusionné : sélecteur de profil riche pour affecter un contrôleur à une EEP DANS le plan
    // (réutilise affecter-controleur-form en mode « plan » ; écrit via l'endpoint plan = auto-inscription).
    path: 'presidence/plans/:id/eep/:enterpriseId/affecter',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    data: { mode: 'plan' },
    loadComponent: () =>
      import('./features/conseil/affectations/affecter-controleur-form').then(
        (m) => m.AffecterControleurFormPage,
      ),
  },
  {
    path: 'presidence/plans/:id',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/plans/plan-detail').then((m) => m.PlanDetailPage),
  },
  {
    // Onglet « طلبات المساندة » du hub « التواصل مع مراقبي الدولة » (voir `presidence/notes-alerte`).
    path: 'presidence/demandes-appui',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    data: { onglet: 'appui' },
    loadComponent: () =>
      import('./features/presidence/echanges/presidence-echanges').then((m) => m.PresidenceEchangesPage),
  },
  {
    path: 'presidence/demandes-appui/:id',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/support/presidence-support-detail').then((m) => m.PresidenceSupportDetailPage),
  },
  {
    path: 'presidence/missions',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/missions/missions-list').then((m) => m.MissionsListPage),
  },
  {
    path: 'presidence/missions/nouvelle',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/missions/mission-form').then((m) => m.MissionFormPage),
  },
  // La CORRECTION du cadrage réutilise le formulaire de lancement (CCE, 2026-08-28) : c'est le même
  // écran, et en tenir deux les ferait diverger au premier champ ajouté d'un seul côté.
  {
    path: 'presidence/missions/:id/modifier',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/missions/mission-form').then((m) => m.MissionFormPage),
  },
  {
    path: 'presidence/missions/:missionId',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/missions/mission-detail').then((m) => m.PresidenceMissionDetailPage),
  },
  // « قاعدة بيانات المؤسسات » (document CCE du 2026-09-04) — la MÊME page dans trois espaces (Président,
  // Cellule de Reporting, Cellule administrative) : la donnée est la même, seul le préfixe change. La
  // garde réelle est serveur (`enterprise:read` portée ALL → 404 sinon ; la cellule administrative la
  // reçoit par la migration 20260904153523).
  {
    path: 'presidence/base-donnees',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/eep/base-donnees/base-donnees-eep-page').then((m) => m.BaseDonneesEepPage),
  },
  {
    path: 'reporting/base-donnees',
    canActivate: [authGuard, roleGuard('CELLULE_REPORTING')],
    loadComponent: () =>
      import('./features/eep/base-donnees/base-donnees-eep-page').then((m) => m.BaseDonneesEepPage),
  },
  {
    path: 'cellule/base-donnees',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () =>
      import('./features/eep/base-donnees/base-donnees-eep-page').then((m) => m.BaseDonneesEepPage),
  },
  {
    path: 'presidence/entreprises',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/entreprises/presidence-entreprises-list').then(
        (m) => m.PresidenceEntreprisesListPage,
      ),
  },
  {
    // Fusion (دمج) ou scission (انقسام) d'EEP — geste du Président (PO 2026-10-15). AVANT `presidence/entreprises/:id` :
    // le routeur résout dans l'ordre de déclaration, et `:id` capturerait « operation ».
    path: 'presidence/entreprises/operation',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/entreprises/operation-eep/operation-eep-page').then((m) => m.OperationEepPage),
  },
  {
    // Fiche entreprise PARTAGÉE avec l'espace contrôleur, en lecture seule pour le Président
    // (l'API renvoie editable=false → aucune action d'édition).
    path: 'presidence/entreprises/:id',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/controleur/entreprise/entreprise-fiche').then((m) => m.EntrepriseFichePage),
  },
  // Espace Conseil (مجلس الهيئة) — VALIDATION du plan d'affectation (ADR 0010 / PO-31) : le Conseil
  // valide (publication automatique) ou renvoie au Président ; idem pour les réaffectations en cours
  // d'année. Composants historiquement sous features/presidence/* (rôle inversé, fichiers non déplacés).
  {
    path: 'conseil/dashboard',
    canActivate: [authGuard, anyRoleGuard('CONSEIL_CCE', 'SECRETARIAT_CONSEIL')],
    loadComponent: () =>
      import('./features/conseil/dashboard/conseil-dashboard').then((m) => m.ConseilDashboardPage),
  },

  // ══ SÉANCES DE LA COMMISSION CAEEP ══════════════════════════════════════════════════════════
  //
  // Les MÊMES composants que le Conseil, montés sous l'espace CAEEP (lot 2 du chantier « réunion
  // CAEEP », 2026-08-03). Réutilisés et NON dupliqués : deux écrans de PV finiraient par diverger, et
  // le lot 23 a déjà réglé dedans le rendu des récusations et le bloc de signatures — le dupliquer
  // reviendrait à devoir corriger deux fois chaque règle.
  //
  // Le cloisonnement ne vient PAS de la route : c'est le serveur qui filtre par type de séance selon
  // la permission (`caeep-seance:read` → CAEEP seulement). Une route protégée par un rôle mais servie
  // par une API non filtrée laisserait tout passer à qui appelle l'API directement.
  //
  // Ordre : les chemins fixes AVANT `:id`, sinon « pv » serait pris pour un identifiant de séance.
  // « تقارير نشاط اللجنة » — le rapport d'activité de la CAEEP (agent CAEEP, 2026-10-31).
  {
    path: 'caeep/rapports-activite',
    canActivate: [authGuard, CAC_ROLES],
    loadComponent: () =>
      import('./features/caeep/cac/cac-rapport-activite').then((m) => m.CacRapportActivitePage),
  },
  {
    path: 'caeep/seances',
    canActivate: [authGuard, anyRoleGuard('SECRETARIAT_CAEEP', 'PRESIDENT_CCE')],
    data: { espace: 'caeep' },
    loadComponent: () =>
      import('./features/conseil/seances/seances-list').then((m) => m.SeancesListPage),
  },
  {
    path: 'caeep/seances/programmation',
    canActivate: [authGuard, anyRoleGuard('SECRETARIAT_CAEEP', 'PRESIDENT_CCE')],
    data: { espace: 'caeep', phase: 'PROGRAMMATION' },
    loadComponent: () =>
      import('./features/conseil/seances/seances-list').then((m) => m.SeancesListPage),
  },
  {
    path: 'caeep/seances/deroule',
    canActivate: [authGuard, anyRoleGuard('SECRETARIAT_CAEEP', 'PRESIDENT_CCE')],
    data: { espace: 'caeep', phase: 'DEROULE' },
    loadComponent: () =>
      import('./features/conseil/seances/seances-list').then((m) => m.SeancesListPage),
  },
  {
    path: 'caeep/seances/pv',
    canActivate: [authGuard, anyRoleGuard('SECRETARIAT_CAEEP', 'PRESIDENT_CCE')],
    data: { espace: 'caeep', phase: 'PV' },
    loadComponent: () =>
      import('./features/conseil/seances/seances-list').then((m) => m.SeancesListPage),
  },
  {
    path: 'caeep/seances/pv/:id',
    canActivate: [authGuard, anyRoleGuard('SECRETARIAT_CAEEP', 'PRESIDENT_CCE')],
    data: { [UN_ECRAN_PAR_DOSSIER]: true, espace: 'caeep' },
    loadComponent: () =>
      import('./features/conseil/seances/seance-pv').then((m) => m.SeancePvPage),
  },
  {
    // La commission a enfin son écran de convocation (revue du 2026-10-05) : il montait le déroulé à la place.
    path: 'caeep/seances/programmation/:id',
    canActivate: [authGuard, anyRoleGuard('SECRETARIAT_CAEEP', 'PRESIDENT_CCE')],
    data: { [UN_ECRAN_PAR_DOSSIER]: true, espace: 'caeep' },
    loadComponent: () =>
      import('./features/conseil/seances/seance-programmation').then((m) => m.SeanceProgrammationPage),
  },
  {
    path: 'caeep/seances/:id',
    canActivate: [authGuard, anyRoleGuard('SECRETARIAT_CAEEP', 'PRESIDENT_CCE')],
    data: { [UN_ECRAN_PAR_DOSSIER]: true, espace: 'caeep' },
    loadComponent: () =>
      import('./features/conseil/seances/seance-detail').then((m) => m.SeanceDetailPage),
  },
  {
    path: 'conseil/seances',
    canActivate: [authGuard, anyRoleGuard('CONSEIL_CCE', 'SECRETARIAT_CONSEIL')],
    loadComponent: () =>
      import('./features/conseil/seances/seances-list').then((m) => m.SeancesListPage),
  },
  // Le cycle de vie d'une réunion (PV du CCE du 24/07/2026) : برمجة → سير → محضر. Depuis la refonte du 2026-10-05,
  // ce sont les trois ÉTAPES d'une page de séance (`app-seance-etapes`) ; seule la liste des محاضر garde sa propre
  // entrée (`phase: 'PV'`, la file du Président). Les وثائق العمل sont un panneau de la séance.
  //
  // Ordre important : ces chemins fixes précèdent `conseil/seances/:id`, sinon « programmation » serait
  // pris pour un identifiant de séance.
  //
  // La PHASE vient avant l'identifiant (`…/programmation/:id`, et non `…/:id/programmation`) : l'adresse se
  // lit alors « la phase, puis la séance », et `routerLinkActive` — qui apparie par PRÉFIXE — garde le menu
  // de la phase allumé quand on ouvre une séance. L'ordre inverse laissait la sidebar sans repère.
  // Une seule entrée « جلسات المجلس » (revue du 2026-10-05) : les anciennes listes par étape mènent à la liste unique.
  { path: 'conseil/seances/programmation', pathMatch: 'full', redirectTo: 'conseil/seances' },
  // Une seule entrée « جلسات المجلس » (revue du 2026-10-05) : les anciennes listes par étape mènent à la liste unique.
  { path: 'conseil/seances/deroule', pathMatch: 'full', redirectTo: 'conseil/seances' },
  {
    path: 'conseil/seances/pv',
    canActivate: [authGuard, anyRoleGuard('CONSEIL_CCE', 'SECRETARIAT_CONSEIL')],
    data: { phase: 'PV' },
    loadComponent: () =>
      import('./features/conseil/seances/seances-list').then((m) => m.SeancesListPage),
  },
  // « وثائق العمل » vit dans la séance (revue du 2026-10-05) : plus de liste à part.
  { path: 'conseil/seances/documents', pathMatch: 'full', redirectTo: 'conseil/seances' },
  // Plus de file de PV sous `/presidence` (CCE, 2026-10-05) : le Président passe d'un espace à l'autre par le
  // sélecteur. Les PV du Conseil se valident et se signent dans l'espace Conseil (« محاضر الجلسة »), ceux de la
  // commission dans l'espace CAEEP (« محاضر اللجنة ») — chaque instance chez elle, comme l'arbitrage le veut.
  {
    path: 'conseil/seances/pv/:id',
    data: { [UN_ECRAN_PAR_DOSSIER]: true },
    canActivate: [authGuard, anyRoleGuard('CONSEIL_CCE', 'SECRETARIAT_CONSEIL')],
    loadComponent: () =>
      import('./features/conseil/seances/seance-pv').then((m) => m.SeancePvPage),
  },
  // Les pièces d'une séance se déposent et se lisent dans ses étapes : l'ancienne adresse ouvre la préparation.
  { path: 'conseil/seances/documents/:id', pathMatch: 'full', redirectTo: 'conseil/seances/programmation/:id' },
  {
    path: 'conseil/seances/programmation/:id',
    data: { [UN_ECRAN_PAR_DOSSIER]: true },
    canActivate: [authGuard, anyRoleGuard('CONSEIL_CCE', 'SECRETARIAT_CONSEIL')],
    loadComponent: () =>
      import('./features/conseil/seances/seance-programmation').then(
        (m) => m.SeanceProgrammationPage,
      ),
  },
  {
    path: 'conseil/seances/:id',
    data: { [UN_ECRAN_PAR_DOSSIER]: true },
    canActivate: [authGuard, anyRoleGuard('CONSEIL_CCE', 'SECRETARIAT_CONSEIL')],
    loadComponent: () =>
      import('./features/conseil/seances/seance-detail').then((m) => m.SeanceDetailPage),
  },
  // Registre des décisions et suivi de leur mise en œuvre (revue de l'espace Conseil, CCE 2026-10-05).
  {
    path: 'conseil/decisions',
    canActivate: [authGuard, anyRoleGuard('CONSEIL_CCE', 'SECRETARIAT_CONSEIL')],
    loadComponent: () =>
      import('./features/conseil/decisions/decisions-registre').then((m) => m.DecisionsRegistrePage),
  },
  {
    path: 'conseil/affectations',
    canActivate: [authGuard, anyRoleGuard('CONSEIL_CCE', 'SECRETARIAT_CONSEIL')],
    loadComponent: () =>
      import('./features/presidence/affectations/affectations-list').then((m) => m.AffectationsListPage),
  },
  {
    path: 'conseil/affectations/:id',
    canActivate: [authGuard, anyRoleGuard('CONSEIL_CCE', 'SECRETARIAT_CONSEIL')],
    loadComponent: () =>
      import('./features/presidence/affectations/affectation-detail').then((m) => m.AffectationDetailPage),
  },
  // L'écran de délibération d'un plan de contrôle HORS séance (`/conseil/plans`) est retiré (revue de l'espace
  // Conseil, CCE 2026-10-05) : il permettait de statuer sans réunion, à rebours de la règle CS-05 (toute validation
  // passe par une séance du Conseil). Les plans se valident en séance ; l'adresse mène à « جدول التعيينات ».
  { path: 'conseil/plans', redirectTo: 'conseil/affectations' },
  { path: 'conseil/plans/:id', redirectTo: 'conseil/affectations' },
  {
    path: 'conseil/reaffectations',
    canActivate: [authGuard, anyRoleGuard('CONSEIL_CCE', 'SECRETARIAT_CONSEIL')],
    loadComponent: () =>
      import('./features/presidence/reaffectations/reaffectations-list').then(
        (m) => m.ReaffectationsListPage,
      ),
  },
  {
    path: 'conseil/reaffectations/:id',
    canActivate: [authGuard, anyRoleGuard('CONSEIL_CCE', 'SECRETARIAT_CONSEIL')],
    loadComponent: () =>
      import('./features/presidence/reaffectations/reaffectation-detail').then(
        (m) => m.ReaffectationDetailPage,
      ),
  },
  // Espace Cellule administrative (الخلية الإدارية) — aval : notification EEP + suivi des AR.
  {
    path: 'cellule/dashboard',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () =>
      import('./features/cellule/dashboard/cellule-dashboard').then((m) => m.CelluleDashboardPage),
  },
  // Espace Cellule de Reporting (espace 06) — dashboard producteur (§5.1).
  {
    path: 'reporting/dashboard',
    canActivate: [authGuard, roleGuard('CELLULE_REPORTING')],
    loadComponent: () =>
      import('./features/reporting/dashboard/reporting-dashboard').then((m) => m.ReportingDashboardPage),
  },
  // Espace Cellule de Reporting — « التقارير الجارية » : les rapports en production, tous
  // producteurs (§5.2, REP-07). Consultation seule : le rôle ne détient que reporting:read ALL.
  // « التقارير الخصوصية » (demande du CCE du 2026-10-20) : les rapports ظرفية et دورية de la هيئة, rédigés hors du SI
  // et déposés ici tels quels. La Cellule dépose ; le Président consulte (même écran, `lecture`).
  {
    path: 'reporting/rapports-specifiques',
    canActivate: [authGuard, roleGuard('CELLULE_REPORTING')],
    loadComponent: () =>
      import('./features/reporting/rapports-specifiques/rapports-specifiques-page').then((m) => m.RapportsSpecifiquesPage),
  },
  {
    path: 'presidence/rapports-specifiques',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    data: { lecture: true },
    loadComponent: () =>
      import('./features/reporting/rapports-specifiques/rapports-specifiques-page').then((m) => m.RapportsSpecifiquesPage),
  },
  {
    path: 'reporting/instances',
    canActivate: [authGuard, roleGuard('CELLULE_REPORTING')],
    loadComponent: () =>
      import('./features/reporting/instances/instances-page').then((m) => m.InstancesPage),
  },
  // Espace Cellule de Reporting — « الاستنتاجات » du Rapport Général (التقرير العام) : les 36 constats
  // du الجزء الثالث §1, calculés depuis la grille de contrôle interne des fiches EEP (étude du
  // 2026-09-16, lot 1). Consultation seule — le gel des chiffres et la rédaction viennent au lot 3.
  {
    // Onglet « التقرير » en tête (Cellule de Reporting, 2026-09-28 : التقرير · إحصائيات · الاستنتاجات · الملاحق) :
    // l'entrée du menu ouvre le document ; les constats ont leur propre adresse.
    path: 'reporting/rapport-general',
    canActivate: [authGuard, roleGuard('CELLULE_REPORTING')],
    loadComponent: () =>
      import('./features/reporting/rapport-general/rapport-general-document').then(
        (m) => m.RapportGeneralDocumentPage,
      ),
  },
  {
    path: 'reporting/rapport-general/constats',
    canActivate: [authGuard, roleGuard('CELLULE_REPORTING')],
    loadComponent: () =>
      import('./features/reporting/rapport-general/rapport-general-constats').then(
        (m) => m.RapportGeneralConstatsPage,
      ),
  },
  // Rapport Général — onglet « الأرقام » : parties I.2, I.3 et II.1 (étude du 2026-09-16, lot 2).
  {
    path: 'reporting/rapport-general/chiffres',
    canActivate: [authGuard, roleGuard('CELLULE_REPORTING')],
    loadComponent: () =>
      import('./features/reporting/rapport-general/rapport-general-chiffres').then(
        (m) => m.RapportGeneralChiffresPage,
      ),
  },
  // Rapport Général — onglet « الملاحق » : les listes nominatives d'EEP publiées en fin de document.
  {
    path: 'reporting/rapport-general/annexes',
    canActivate: [authGuard, roleGuard('CELLULE_REPORTING')],
    loadComponent: () =>
      import('./features/reporting/rapport-general/rapport-general-annexes').then(
        (m) => m.RapportGeneralAnnexesPage,
      ),
  },
  // Rapport Général — onglet « المستند » : production, gel des chiffres et manuscrit Word (lot 3).
  {
    // L'ancienne adresse de l'onglet (« المستند ») : les liens déjà partagés mènent au même écran.
    path: 'reporting/rapport-general/document',
    redirectTo: 'reporting/rapport-general',
  },
  // « /reporting/indicateurs » (المؤشرات) et « /reporting/documents » (الوثائق) ont été retirées le
  // 2026-09-04 à la demande de la Cellule de Reporting — voir `reportingGroups` dans main-layout.
  // Espace Cellule de Reporting — « تصدير حسب الطلب », pivot ad hoc (module 12 §15.6, ADR 0011).
  {
    path: 'reporting/explorateur',
    canActivate: [authGuard, roleGuard('CELLULE_REPORTING')],
    loadComponent: () =>
      import('./features/reporting/explorateur/explorateur').then((m) => m.ExplorateurPage),
  },
  // Espace Cellule de Reporting — référentiel des EEP, comme côté Président (demande du
  // 2026-08-20) : MÊMES écrans, lecture seule (l'API renvoie `editable=false` hors contrôleur
  // affecté). La garde réelle est serveur : `enterprise:read` portée ALL, accordée par la migration
  // 20260820132400. La fiche n'affiche pas de lien vers les missions ici — cet espace n'en porte
  // pas d'écran, et `missionsBase` vide masque le lien plutôt que de mener à une garde de rôle.
  {
    path: 'reporting/entreprises',
    canActivate: [authGuard, roleGuard('CELLULE_REPORTING')],
    loadComponent: () =>
      import('./features/presidence/entreprises/presidence-entreprises-list').then(
        (m) => m.PresidenceEntreprisesListPage,
      ),
  },
  {
    path: 'reporting/entreprises/:id',
    canActivate: [authGuard, roleGuard('CELLULE_REPORTING')],
    loadComponent: () =>
      import('./features/controleur/entreprise/entreprise-fiche').then((m) => m.EntrepriseFichePage),
  },
  // Annuaire des contrôleurs — le MÊME écran que côté Président et cellule administrative, sous le
  // préfixe du reporting : la donnée est la même, seul l'espace d'où on l'atteint change (demande du
  // responsable d'espace, 2026-08-20). La vraie garde est SERVEUR (`controller:read` portée ALL,
  // refus en 404, accordée par la migration 20260820120135) ; la garde de rôle n'est là que pour ne
  // pas afficher un écran vide à qui n'a rien à y voir.
  {
    path: 'reporting/controleurs',
    canActivate: [authGuard, roleGuard('CELLULE_REPORTING')],
    loadComponent: () =>
      import('./features/annuaire/controleurs-page').then((m) => m.ControleursPage),
  },
  {
    path: 'reporting/controleurs/:id',
    canActivate: [authGuard, roleGuard('CELLULE_REPORTING')],
    loadComponent: () =>
      import('./features/annuaire/controleur-fiche').then((m) => m.ControleurFichePage),
  },
  {
    path: 'cellule/cooperation',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () =>
      import('./features/cellule/cooperation/cooperation-page').then(
        (m) => m.CooperationPage,
      ),
  },
  // Fiche projet de coopération (page dédiée). `:id === 'nouveau'` → mode création.
  {
    path: 'cellule/cooperation/projets/:id',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () =>
      import('./features/cellule/cooperation/cooperation-projet-detail').then(
        (m) => m.CooperationProjetDetail,
      ),
  },
  // Annuaire des contrôleurs (AD-02) — le MÊME écran que côté Président, sous le préfixe de la
  // cellule : la donnée est la même, seul l'espace d'où on l'atteint change.
  {
    path: 'cellule/controleurs',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () =>
      import('./features/annuaire/controleurs-page').then((m) => m.ControleursPage),
  },
  {
    path: 'cellule/controleurs/:id',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () =>
      import('./features/annuaire/controleur-fiche').then((m) => m.ControleurFichePage),
  },
  // Gestion des comptes (07/09) : création, modification, activation, rôles, mot de passe — cellule administrative.
  {
    path: 'cellule/comptes',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () => import('./features/cellule/comptes/comptes-page').then((m) => m.ComptesPage),
  },
  // Formation (التكوين) — plan + suivi d'exécution (espace Cellule, §8bis.1).
  {
    path: 'cellule/formations',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () =>
      import('./features/cellule/formation/formations-page').then((m) => m.FormationsPage),
  },
  // Formation : création (formulaire).
  {
    path: 'cellule/formations/nouveau',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () =>
      import('./features/cellule/formation/formation-detail').then((m) => m.FormationDetailPage),
  },
  // Formation : édition (formulaire) — derrière le bouton « Modifier » de la vue.
  {
    path: 'cellule/formations/:id/edit',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () =>
      import('./features/cellule/formation/formation-detail').then((m) => m.FormationDetailPage),
  },
  // Formation : VUE détaillée (lecture seule) — page d'atterrissage au clic sur une formation.
  {
    path: 'cellule/formations/:id',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () =>
      import('./features/cellule/formation/formation-view').then((m) => m.FormationViewPage),
  },
  // سجل التعيينات (2026-09-24) : le registre des affectations, rectifiable par la cellule administrative
  // SEULE (arbitrage PO) — le serveur garde `assignment:correct` ALL ; la garde de rôle évite l'écran vide.
  {
    path: 'cellule/affectations',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () =>
      import('./features/cellule/affectations/registre-affectations-page').then((m) => m.RegistreAffectationsPage),
  },
  // Suivi des تعويض مؤقت en lecture, et leurs actes (مذكرة تعويض, إستئناف عمل) : la cellule prépare le courrier du
  // Président et l'archive (CCE, appel du 27/10/2026).
  {
    path: 'cellule/remplacements',
    data: { espace: 'cellule' },
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () =>
      import('./features/affectation-shared/remplacements/remplacements-list').then((m) => m.RemplacementsListPage),
  },
  // Séances de travail & calcul de primes (extract annuel, §8bis.3 — accès restreint jeton:*).
  {
    path: 'cellule/primes',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () => import('./features/cellule/primes/primes-page').then((m) => m.PrimesPage),
  },
  // Restitution institutionnelle de la Cellule (CELL-13) : formation + coopération + séances de
  // travail d'un même exercice, sous un seul sélecteur d'année, exportables pour le rapport annuel.
  {
    path: 'cellule/bilan',
    canActivate: [authGuard, roleGuard('CELLULE_ADMINISTRATIVE')],
    loadComponent: () =>
      import('./features/cellule/bilan/bilan-annuel').then((m) => m.BilanAnnuelPage),
  },
  // Contrôleur — inbox d'affectations à accuser (Écran 8, الإسناد والإشعار بالاستلام).
  {
    path: 'controleur/affectations',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/affectation/affectation-inbox').then((m) => m.AffectationInboxPage),
  },
  // Passation de missions (نقل المهام) — la passation autonome est supprimée (pivot #109) : le rapport
  // est un sous-processus documentaire de la réaffectation, accessible via l'inbox d'affectation
  // (الإسناد والإشعار بالاستلام, section passations). Seule la FICHE (détail à remplir) subsiste.
  {
    path: 'controleur/passations/:id',
    data: { [UN_ECRAN_PAR_DOSSIER]: true },
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/controleur/passation/passation-detail').then((m) => m.PassationDetailPage),
  },
  // Passation — revue + visa du Président (VISA_PENDING → COMPLETED). Inbox sans :id, revue avec :id.
  {
    // La file de suivi des passations (tableau de suivi, 2026-10-02) ; la fiche reste `passation-review`.
    path: 'presidence/passations',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/passations/passations-list').then((m) => m.PassationsListPage),
  },
  // « متابعة عمليات التعويض المؤقت » : les تعويض مؤقت que le Président décide seul (CCE, appel du 27/10/2026).
  {
    path: 'presidence/remplacements',
    data: { espace: 'presidence' },
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/affectation-shared/remplacements/remplacements-list').then((m) => m.RemplacementsListPage),
  },
  {
    path: 'presidence/passations/:id',
    data: { [UN_ECRAN_PAR_DOSSIER]: true },
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () =>
      import('./features/presidence/passations/passation-review').then((m) => m.PassationReviewPage),
  },
  // PR-04 (CR 2026-07-24) : فضاء التواصل — messagerie Président ↔ contrôleurs. Écran UNIQUE monté
  // dans les deux espaces (la sidebar est contextuelle au préfixe d'URL) ; seule la création est
  // réservée au Président, et c'est l'API qui fait autorité.
  {
    path: 'controleur/communication',
    canActivate: [authGuard, roleGuard('CONTROLEUR_ETAT')],
    loadComponent: () =>
      import('./features/communication/communication-page').then((m) => m.CommunicationPage),
  },
  {
    // Côté Président, la messagerie est l'onglet « المحادثات » du hub « التواصل مع مراقبي الدولة » ; l'icône
    // de la barre du haut mène toujours ici.
    path: 'presidence/communication',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    data: { onglet: 'messagerie' },
    loadComponent: () =>
      import('./features/presidence/echanges/presidence-echanges').then((m) => m.PresidenceEchangesPage),
  },
  // TR-03 (CR 2026-07-24) : 9ᵉ espace « Qualité & Évaluation » — contrôle qualité périodique et par
  // échantillonnage. UN seul espace pour les DEUX organes tranchés au cadrage du 15/06 (فريق رقابة
  // الجودة et خلية المراجعة الداخلية) : ils partagent l'outillage et ne diffèrent que par le
  // périmètre et les permissions. Le Président y accède aussi — c'est lui qui pilote le dispositif
  // et qui signe la conclusion annuelle (décision R1).
  {
    path: 'qualite/campagnes',
    canActivate: [authGuard, anyRoleGuard('EQUIPE_QUALITE', 'CELLULE_REVISION_INTERNE', 'PRESIDENT_CCE', 'ADMIN_FONCTIONNEL')],
    loadComponent: () => import('./features/qualite/cockpit-page').then((m) => m.QualiteCockpitPage),
  },
  {
    path: 'qualite/campagnes/:id/echantillon',
    canActivate: [authGuard, anyRoleGuard('EQUIPE_QUALITE', 'CELLULE_REVISION_INTERNE', 'PRESIDENT_CCE')],
    loadComponent: () => import('./features/qualite/echantillon-page').then((m) => m.QualiteEchantillonPage),
  },
  {
    path: 'qualite/campagnes/:id/matrice',
    canActivate: [authGuard, anyRoleGuard('EQUIPE_QUALITE', 'CELLULE_REVISION_INTERNE', 'PRESIDENT_CCE', 'ADMIN_FONCTIONNEL')],
    loadComponent: () => import('./features/qualite/matrice-page').then((m) => m.QualiteMatricePage),
  },
  {
    path: 'qualite/revues',
    canActivate: [authGuard, anyRoleGuard('EQUIPE_QUALITE', 'CELLULE_REVISION_INTERNE', 'PRESIDENT_CCE')],
    loadComponent: () => import('./features/qualite/revue-page').then((m) => m.QualiteRevuePage),
  },
  // Les MÊMES écrans, montés sous le préfixe du Président : la sidebar étant contextuelle à l'URL,
  // c'est la seule façon qu'il consulte le dispositif qualité SANS quitter son espace ni perdre son
  // menu. Précédent identique : le فضاء التواصل, monté dans les deux espaces.
  {
    path: 'presidence/qualite/campagnes',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () => import('./features/qualite/cockpit-page').then((m) => m.QualiteCockpitPage),
  },
  {
    path: 'presidence/qualite/campagnes/:id/echantillon',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () => import('./features/qualite/echantillon-page').then((m) => m.QualiteEchantillonPage),
  },
  {
    path: 'presidence/qualite/campagnes/:id/matrice',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () => import('./features/qualite/matrice-page').then((m) => m.QualiteMatricePage),
  },
  {
    path: 'presidence/qualite/revues',
    canActivate: [authGuard, roleGuard('PRESIDENT_CCE')],
    loadComponent: () => import('./features/qualite/revue-page').then((m) => m.QualiteRevuePage),
  },
  { path: '**', redirectTo: 'controleur/dashboard' },
];
